/**
 * Stub Provider Implementations
 *
 * These are mock implementations that log to console.
 * Replace with real implementations when integrating with the app.
 */

import {
  NavigationProvider,
  ModalProvider,
  ChatProvider,
  NotificationProvider,
  UIProvider,
  DataProvider,
  AnalyticsProvider,
  FlowProviders,
  ChatMessage,
  FileUploadResult,
  NotificationType,
} from '../types';

// ============================================================================
// Stub Navigation Provider
// ============================================================================

export class StubNavigationProvider implements NavigationProvider {
  async navigateToView(view: string, params?: Record<string, any>): Promise<void> {
    console.log('[Stub Navigation] Navigate to view:', view, params);
    // In real implementation: router.push(`/view/${view}`, { state: params })
  }

  async navigateTo(path: string): Promise<void> {
    console.log('[Stub Navigation] Navigate to path:', path);
    // In real implementation: router.push(path)
  }

  async goBack(): Promise<void> {
    console.log('[Stub Navigation] Go back');
    // In real implementation: router.back()
  }

  getCurrentRoute() {
    console.log('[Stub Navigation] Get current route');
    return {
      path: '/demo',
      view: 'dashboard',
      params: {},
    };
    // In real implementation: return { path: router.pathname, params: router.params }
  }
}

// ============================================================================
// Stub Modal Provider
// ============================================================================

export class StubModalProvider implements ModalProvider {
  async openUploadModal(options?: any): Promise<void> {
    console.log('[Stub Modal] Open upload modal:', options);
    // In real implementation: setUploadModalOpen(true); setUploadModalProps(options)
  }

  async closeUploadModal(): Promise<void> {
    console.log('[Stub Modal] Close upload modal');
    // In real implementation: setUploadModalOpen(false)
  }

  async openShareModal(contractId: string): Promise<void> {
    console.log('[Stub Modal] Open share modal for contract:', contractId);
    // In real implementation: setShareModalOpen(true); setSelectedContract(contractId)
  }

  async openDealCreationModal(): Promise<void> {
    console.log('[Stub Modal] Open deal creation modal');
    // In real implementation: setDealCreationModalOpen(true)
  }

  async openModal(modalId: string, props?: any): Promise<void> {
    console.log('[Stub Modal] Open modal:', modalId, props);
    // In real implementation: modalRegistry[modalId].open(props)
  }

  async closeModal(modalId?: string): Promise<void> {
    console.log('[Stub Modal] Close modal:', modalId);
    // In real implementation: closeAllModals() or modalRegistry[modalId].close()
  }
}

// ============================================================================
// Stub Chat Provider
// ============================================================================

export class StubChatProvider implements ChatProvider {
  private messages: ChatMessage[] = [];

  async displayMessage(message: ChatMessage, options?: any): Promise<void> {
    console.log('[Stub Chat] Display message:', message, options);
    this.messages.push(message);

    if (options?.typing) {
      await this.showTyping(options.typingDelay);
    }

    // In real implementation:
    // chatContext.addMessage(message)
    // if (options.typing) await sleep(options.typingDelay)
  }

  async showTyping(duration: number = 1000): Promise<void> {
    console.log('[Stub Chat] Show typing for', duration, 'ms');
    return new Promise(resolve => setTimeout(resolve, duration));
    // In real implementation: setIsTyping(true); setTimeout(() => setIsTyping(false), duration)
  }

  hideTyping(): void {
    console.log('[Stub Chat] Hide typing');
    // In real implementation: setIsTyping(false)
  }

  clearChat(): void {
    console.log('[Stub Chat] Clear chat');
    this.messages = [];
    // In real implementation: chatContext.clearMessages()
  }

  getChatHistory(): ChatMessage[] {
    return [...this.messages];
    // In real implementation: return chatContext.messages
  }
}

// ============================================================================
// Stub Notification Provider
// ============================================================================

export class StubNotificationProvider implements NotificationProvider {
  showNotification(message: string, type: NotificationType, duration?: number): void {
    console.log(`[Stub Notification] ${type.toUpperCase()}:`, message, `(${duration}ms)`);
    // In real implementation: toast[type](message, { duration })
  }

  showSuccess(message: string, duration?: number): void {
    this.showNotification(message, 'success', duration);
  }

  showError(message: string, duration?: number): void {
    this.showNotification(message, 'error', duration);
  }

  showWarning(message: string, duration?: number): void {
    this.showNotification(message, 'warning', duration);
  }

  async showCelebration(message?: string): Promise<void> {
    console.log('[Stub Notification] 🎉 Celebration!', message);
    // In real implementation:
    // import confetti from 'canvas-confetti'
    // confetti({ particleCount: 100, spread: 70 })
    // if (message) toast.success(message)
  }
}

// ============================================================================
// Stub UI Provider
// ============================================================================

export class StubUIProvider implements UIProvider {
  private highlights: Set<string> = new Set();

  async highlightElement(selector: string, options?: any): Promise<() => void> {
    console.log('[Stub UI] Highlight element:', selector, options);
    this.highlights.add(selector);

    // Return cleanup function
    return () => {
      console.log('[Stub UI] Remove highlight:', selector);
      this.highlights.delete(selector);
    };

    // In real implementation:
    // const element = document.querySelector(selector)
    // element.classList.add('highlighted')
    // return () => element.classList.remove('highlighted')
  }

  removeHighlight(highlightId: string): void {
    console.log('[Stub UI] Remove highlight by ID:', highlightId);
    this.highlights.delete(highlightId);
    // In real implementation:
    // Remove the highlight overlay/effect by ID
  }

  async showTooltip(selector: string, content: string, position?: string): Promise<() => void> {
    console.log('[Stub UI] Show tooltip:', selector, content, position);

    return () => {
      console.log('[Stub UI] Remove tooltip:', selector);
    };

    // In real implementation:
    // tooltipService.show(selector, content, position)
    // return () => tooltipService.hide(selector)
  }

  async scrollToElement(selector: string, options?: any): Promise<void> {
    console.log('[Stub UI] Scroll to element:', selector, options);
    // In real implementation:
    // document.querySelector(selector)?.scrollIntoView(options)
  }

  disableInteractions(): void {
    console.log('[Stub UI] Disable interactions');
    // In real implementation:
    // document.body.style.pointerEvents = 'none'
    // document.querySelector('.flow-container').style.pointerEvents = 'auto'
  }

  enableInteractions(): void {
    console.log('[Stub UI] Enable interactions');
    // In real implementation:
    // document.body.style.pointerEvents = 'auto'
  }

  simulateClick(selector: string): void {
    console.log('[Stub UI] Simulate click on:', selector);
    // In real implementation:
    // const element = document.querySelector(selector);
    // if (element) element.click();
  }

  elementExists(selector: string): boolean {
    console.log('[Stub UI] Check element exists:', selector);
    return true;
    // In real implementation:
    // return document.querySelector(selector) !== null
  }

  async waitForElement(selector: string, timeout: number = 5000): Promise<Element> {
    console.log('[Stub UI] Wait for element:', selector, timeout);

    // Stub: just return a fake element after short delay
    await new Promise(resolve => setTimeout(resolve, 100));
    return document.createElement('div');

    // In real implementation:
    // return new Promise((resolve, reject) => {
    //   const observer = new MutationObserver(() => {
    //     const element = document.querySelector(selector)
    //     if (element) {
    //       observer.disconnect()
    //       resolve(element)
    //     }
    //   })
    //   observer.observe(document.body, { childList: true, subtree: true })
    //   setTimeout(() => {
    //     observer.disconnect()
    //     reject(new Error(`Element not found: ${selector}`))
    //   }, timeout)
    // })
  }
}

// ============================================================================
// Stub Data Provider
// ============================================================================

export class StubDataProvider implements DataProvider {
  async uploadFile(file: any): Promise<FileUploadResult> {
    console.log('[Stub Data] Upload file:', file.name || file);

    // Simulate upload delay
    await new Promise(resolve => setTimeout(resolve, 500));

    return {
      fileId: `file-${Date.now()}`,
      fileName: file.name || 'unknown',
      fileSize: file.size || 0,
      fileType: file.type || 'application/pdf',
      url: `https://example.com/files/${file.name || 'file'}`,
    };

    // In real implementation:
    // const formData = new FormData()
    // formData.append('file', file)
    // const response = await api.post('/upload', formData)
    // return response.data
  }

  async uploadFiles(files: File[]): Promise<FileUploadResult[]> {
    console.log('[Stub Data] Upload files:', files.map(f => f.name));

    // Simulate upload delay
    await new Promise(resolve => setTimeout(resolve, 1500));

    return files.map((file, i) => ({
      fileId: `file-${i}-${Date.now()}`,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      url: `https://example.com/files/${file.name}`,
    }));

    // In real implementation:
    // const formData = new FormData()
    // files.forEach(file => formData.append('files', file))
    // const response = await api.post('/upload', formData)
    // return response.data
  }

  async fetchContract(contractId: string): Promise<any> {
    console.log('[Stub Data] Fetch contract:', contractId);

    // Return mock data
    return {
      id: contractId,
      name: 'Artist Recording Agreement',
      type: 'recording',
      status: 'complete',
      // ... more fields
    };

    // In real implementation:
    // const response = await api.get(`/contracts/${contractId}`)
    // return response.data
  }

  async fetchDeal(dealId: string): Promise<any> {
    console.log('[Stub Data] Fetch deal:', dealId);

    return {
      id: dealId,
      name: 'Sony Music Partnership',
      status: 'in_progress',
      // ... more fields
    };

    // In real implementation:
    // const response = await api.get(`/deals/${dealId}`)
    // return response.data
  }

  async createDeal(data: any): Promise<{ dealId: string }> {
    console.log('[Stub Data] Create deal:', data);

    return {
      dealId: `deal-${Date.now()}`,
    };

    // In real implementation:
    // const response = await api.post('/deals', data)
    // return response.data
  }

  async updateDeal(dealId: string, data: any): Promise<void> {
    console.log('[Stub Data] Update deal:', dealId, data);

    // In real implementation:
    // await api.patch(`/deals/${dealId}`, data)
  }

  async submitForm(formData: any): Promise<any> {
    console.log('[Stub Data] Submit form:', formData);

    // Simulate form submission delay
    await new Promise(resolve => setTimeout(resolve, 800));

    return {
      success: true,
      message: 'Form submitted successfully',
      data: formData,
    };

    // In real implementation:
    // const response = await api.post('/form-submit', formData)
    // return response.data
  }

  async shareContract(contractId: string, userEmail: string): Promise<void> {
    console.log('[Stub Data] Share contract:', contractId, 'with', userEmail);

    // In real implementation:
    // await api.post(`/contracts/${contractId}/share`, { email: userEmail })
  }
}

// ============================================================================
// Stub Analytics Provider
// ============================================================================

export class StubAnalyticsProvider implements AnalyticsProvider {
  track(eventName: string, properties?: Record<string, any>): void {
    console.log('[Stub Analytics] Track event:', eventName, properties);
    // In real implementation:
    // posthog.capture(eventName, properties)
  }

  trackPageView(pageName: string, properties?: Record<string, any>): void {
    console.log('[Stub Analytics] Track page view:', pageName, properties);
    // In real implementation:
    // posthog.capture('$pageview', { page: pageName, ...properties })
  }

  identify(userId: string, traits?: Record<string, any>): void {
    console.log('[Stub Analytics] Identify user:', userId, traits);
    // In real implementation:
    // posthog.identify(userId, traits)
  }
}

// ============================================================================
// Create Default Stub Providers
// ============================================================================

export function createStubProviders(): FlowProviders {
  return {
    navigation: new StubNavigationProvider(),
    modals: new StubModalProvider(),
    chat: new StubChatProvider(),
    notifications: new StubNotificationProvider(),
    ui: new StubUIProvider(),
    data: new StubDataProvider(),
    analytics: new StubAnalyticsProvider(),
  };
}
